import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";

import { useOffSetTop } from "src/hooks/use-off-set-top";
import { useResponsive } from "src/hooks/use-responsive";

import { bgBlur } from "src/theme/css";

import Logo from "src/components/logo";
import SvgColor from "src/components/svg-color";
import { useSettingsContext } from "src/components/settings";

import { NAV, HEADER } from "../config-layout";
import LanguagePopover from "../common/language-popover";
import MessagesIcon from "../common/messages-icon";
import { useEffect, useState } from "react";
import ProfileSelector from "src/components/profile-selector";
import Iconify from "src/components/iconify";
import { useAuthContext } from "src/auth/hooks";
import { useSnackbar } from "src/components/snackbar";
import { useRouter } from "src/routes/hooks";
import { t } from "i18next";
import {
  CircularProgress,
  Tooltip,
  Typography,
  Popover,
  Box,
  Alert,
} from "@mui/material";
import { useGetUserProviderProfiles } from "src/api/provider";
import { IProfileSelectorProvider } from "src/types/sportprovider";
import PublicProfileIcon from "../common/public-profile-icon";
import AdminOnboardingEntry from "../common/admin-onboarding-dialog";
// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const router = useRouter();
  const settings = useSettingsContext();

  const {
    currentProvider,
    currentProviderType,
    logout,
    updateProvider,
  } = useAuthContext();

  const { providers, providersLoading, providersError } =
    useGetUserProviderProfiles();

  const { enqueueSnackbar } = useSnackbar();

  const isNavHorizontal = settings.themeLayout === "horizontal";

  const isNavMini = settings.themeLayout === "mini";

  const lgUp = useResponsive("up", "lg");

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [helpRequest, setHelpRequest] = useState(0);
  const hasOnboarding = ["facility", "coach", "organiser"].includes(currentProviderType);
  const helpLabel = t(hasOnboarding ? "admin_onboarding_help" : "help");

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleLogout = async () => {
    try {
      await logout();

      router.replace("/");
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  // Refresh session if there's an error
  useEffect(() => {
    if (!providersLoading && providers.length > 0) {
      const storedProvider =
        currentProvider ||
        parseInt(localStorage.getItem("currentProvider") || "0", 10);

      const validProvider = providers.find(
        (p: IProfileSelectorProvider) => p.id === storedProvider
      );

      if (validProvider) {
        updateProvider(validProvider.id, validProvider.type);
      } else {
        updateProvider(providers[0].id, providers[0].type);
      }
    }
  }, [providers, providersLoading]);

  const isMobile = !lgUp;

  const NavItem = ({
    children,
    label,
  }: {
    children: React.ReactNode;
    label: string;
  }) => (
    <Stack
      direction="column"
      alignItems="center"
      spacing={0.3}
      sx={{
        minWidth: isMobile ? 60 : "auto",
      }}
    >
      {children}
      {isMobile && (
        <Typography variant="caption" sx={{ fontSize: 11, opacity: 0.8 }}>
          {label}
        </Typography>
      )}
    </Stack>
  );

  // Your content Stack
  const NavigationIconsContent = (
    <Stack
      direction={isMobile ? "column" : "row"}
      alignItems="center"
      justifyContent="flex-end"
      spacing={isMobile ? 2 : 1}
    >
      {isMobile && <Alert severity="info">{t("tap_on_icons")}</Alert>}
      <NavItem label={helpLabel}>
        <Tooltip title={helpLabel}>
          <IconButton aria-label={helpLabel} onClick={() => { handleClose(); setHelpRequest((value) => value + 1); }} sx={{ color: "info.main", pt: 0.5 }}>
            <Iconify icon={hasOnboarding ? "solar:notebook-bold-duotone" : "mingcute:question-line"} />
          </IconButton>
        </Tooltip>
      </NavItem>
      {!providersLoading && !providersError && currentProvider && (
        <>
          <NavItem label={t("public_profile")}>
            <PublicProfileIcon
              provider={providers.find(
                (provider: any) => provider.id === currentProvider
              )}
            />
          </NavItem>
          <NavItem label={t("messages")}>
            <MessagesIcon currentProvider={currentProvider} />
          </NavItem>
        </>
      )}
      <NavItem label={t("language")}>
        <LanguagePopover />
      </NavItem>
      <NavItem label={t("playermenu_logout")}>
        <Tooltip title={t("playermenu_logout")}>
          <IconButton onClick={handleLogout} sx={{ width: 40, height: 40 }}>
            <Iconify
              icon="material-symbols-light:logout-sharp"
              color={"black"}
            />
          </IconButton>
        </Tooltip>
      </NavItem>
    </Stack>
  );

  const renderContent = (
    <>
      <Stack flexGrow={1} direction="row" spacing={{ xs: 0.5, sm: 0.5 }}>
        {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

        {!lgUp && (
          <IconButton onClick={onOpenNav}>
            <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
          </IconButton>
        )}

        {providersLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", py: 4 }}
          >
            <CircularProgress />
          </Stack>
        )}

        {!providersLoading && !providersError && providers && (
          <ProfileSelector
            currentProvider={currentProvider}
            currentProviderType={currentProviderType}
            providers={providers}
            updateProvider={updateProvider}
          />
        )}
      </Stack>
      {/* <Searchbar /> */}

      {/* Popover with your Stack inside */}
      {!lgUp ? (
        <>
          {/* Trigger button only on mobile */}
          <IconButton aria-label={t("dashboard_icon_menu")} aria-haspopup="true" aria-expanded={open}
            aria-controls={open ? "dashboard-icon-menu" : undefined} onClick={handleOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>

          <Popover
            id="dashboard-icon-menu"
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { p: 2, minWidth: 200 } }}
          >
            {NavigationIconsContent}
          </Popover>
        </>
      ) : (
        // Inline rendering on desktop
        <Box>{NavigationIconsContent}</Box>
      )}
    </>
  );

  return (
    <><AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(["height"], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: "background.default",
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
          background: "white",
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
      <AdminOnboardingEntry
        hideTrigger request={helpRequest}
        providerName={providers.find((provider: IProfileSelectorProvider) => provider.id === currentProvider)?.displayName}
        providerReady={!providersLoading && providers.some((provider: IProfileSelectorProvider) => provider.id === currentProvider)}
      />
    </>
  );
}
